import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Database, Upload, Loader2, Search, ExternalLink, RefreshCcw, X, ClipboardPaste } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PRICE_COLS = [
  { key: 'precio_m2_vivienda', label: 'Vivienda' },
  { key: 'precio_m2_piso',     label: 'Piso / Apto' },
  { key: 'precio_m2_casa',     label: 'Casa / Chalet' },
  { key: 'precio_m2_alquiler', label: 'Alquiler' },
];

// URL builder for Idealista valoracion pages.
// Mapping extracted from the official directory:
//   https://www.idealista.com/valoracion-de-inmuebles/
// Provincia pages use different suffixes; some have "-provincia", others don't.
// Municipio pages use the short form (no "-provincia" suffix) as the tail.
// Example: Aspe in Alicante -> aspe-alicante
//          Móstoles in Madrid -> mostoles-madrid
const slugify = (s) => (s || '')
  .toString()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')   // strip accents
  .toLowerCase()
  .replace(/[^a-z0-9\s-]/g, '')      // drop punctuation (parens, slashes, commas)
  .trim()
  .replace(/\s+/g, '-')              // spaces -> hyphens
  .replace(/-+/g, '-');              // collapse duplicate hyphens

// Official provincia URL map (provincia display name normalized → full slug used
// by the /valoracion-de-inmuebles/<SLUG> page). The municipio URL uses the same
// slug but stripped of any "-provincia" suffix.
const IDEALISTA_PROV_URL = {
  'a coruna':                'a-coruna-provincia',
  'la coruna':               'a-coruna-provincia',
  'coruna':                  'a-coruna-provincia',
  'alava':                   'alava',
  'araba':                   'alava',
  'araba alava':             'alava',
  'albacete':                'albacete-provincia',
  'alicante':                'alicante',
  'alacant':                 'alicante',
  'alicante alacant':        'alicante',
  'alacant alicante':        'alicante',
  'almeria':                 'almeria-provincia',
  'asturias':                'asturias',
  'avila':                   'avila-provincia',
  'badajoz':                 'badajoz-provincia',
  'balears illes':           'balears-illes',
  'illes balears':           'balears-illes',
  'balears':                 'balears-illes',
  'baleares':                'balears-illes',
  'barcelona':               'barcelona-provincia',
  'burgos':                  'burgos-provincia',
  'caceres':                 'caceres-provincia',
  'cadiz':                   'cadiz-provincia',
  'cantabria':               'cantabria',
  'castellon':               'castellon',
  'castello':                'castellon',
  'castellon castello':      'castellon',
  'castello castellon':      'castellon',
  'ceuta':                   'ceuta-provincia',
  'ciudad real':             'ciudad-real-provincia',
  'cordoba':                 'cordoba-provincia',
  'cuenca':                  'cuenca-provincia',
  'girona':                  'girona-provincia',
  'gerona':                  'girona-provincia',
  'granada':                 'granada-provincia',
  'guadalajara':             'guadalajara-provincia',
  'guipuzcoa':               'guipuzcoa',
  'gipuzkoa':                'guipuzcoa',
  'huelva':                  'huelva-provincia',
  'huesca':                  'huesca-provincia',
  'jaen':                    'jaen-provincia',
  'la rioja':                'la-rioja',
  'rioja':                   'la-rioja',
  'las palmas':              'las-palmas',
  'leon':                    'leon-provincia',
  'lleida':                  'lleida-provincia',
  'lerida':                  'lleida-provincia',
  'lugo':                    'lugo-provincia',
  'madrid':                  'madrid-provincia',
  'malaga':                  'malaga-provincia',
  'melilla':                 'melilla-provincia',
  'murcia':                  'murcia-provincia',
  'navarra':                 'navarra',
  'nafarroa':                'navarra',
  'ourense':                 'ourense-provincia',
  'orense':                  'ourense-provincia',
  'palencia':                'palencia-provincia',
  'pontevedra':              'pontevedra-provincia',
  'salamanca':               'salamanca-provincia',
  'santa cruz de tenerife':  'santa-cruz-de-tenerife-provincia',
  'tenerife':                'santa-cruz-de-tenerife-provincia',
  'segovia':                 'segovia-provincia',
  'sevilla':                 'sevilla-provincia',
  'soria':                   'soria-provincia',
  'tarragona':               'tarragona-provincia',
  'teruel':                  'teruel-provincia',
  'toledo':                  'toledo-provincia',
  'valencia':                'valencia-provincia',
  'valencia valencia':       'valencia-provincia',
  'valladolid':              'valladolid-provincia',
  'vizcaya':                 'vizcaya',
  'bizkaia':                 'vizcaya',
  'zamora':                  'zamora-provincia',
  'zaragoza':                'zaragoza-provincia',
};

const normProv = (s) => (s || '')
  .toString()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[\/,()]/g, ' ')      // "Alicante/Alacant" -> "Alicante Alacant"
  .replace(/\s+/g, ' ')
  .trim();

// Returns { full, short } for a provincia, or null if unknown
const resolveProvSlugs = (provincia) => {
  if (!provincia) return null;
  const norm = normProv(provincia);
  // 1) Try exact normalized match
  let full = IDEALISTA_PROV_URL[norm];
  // 2) Try first token (e.g., "alicante alacant" -> "alicante")
  if (!full) {
    const first = norm.split(' ')[0];
    full = IDEALISTA_PROV_URL[first];
  }
  // 3) Try slashed primary (e.g., keep before "/")
  if (!full && provincia.includes('/')) {
    const primary = normProv(provincia.split('/')[0]);
    full = IDEALISTA_PROV_URL[primary];
  }
  if (!full) {
    // Last resort: slugify directly. Likely works for unknown names.
    full = slugify(provincia.split('/')[0]);
  }
  const short = full.replace(/-provincia$/, '');
  return { full, short };
};

const idealistaProvUrl = (provincia) => {
  const r = resolveProvSlugs(provincia);
  return r ? `https://www.idealista.com/valoracion-de-inmuebles/${r.full}` : null;
};

// For municipios with bilingual "Nombre_es / Nombre_ca" format, take first form
const munSlug = (municipio) => {
  if (!municipio) return '';
  const primary = municipio.split('/')[0]; // "Alicante / Alacant" -> "Alicante"
  return slugify(primary);
};

const idealistaMunUrl = (provincia, municipio) => {
  const provR = resolveProvSlugs(provincia);
  const ms = munSlug(municipio);
  if (!provR || !ms) return null;
  // If municipio name = provincia short slug, return the provincia page
  if (ms === provR.short) return `https://www.idealista.com/valoracion-de-inmuebles/${provR.full}`;
  return `https://www.idealista.com/valoracion-de-inmuebles/${ms}-${provR.short}`;
};

const formatPrice = (v) => v != null && v !== '' ? `${Number(v).toLocaleString('es-ES')}` : '-';
const formatDate = (d) => {
  if (!d) return '-';
  try {
    // Accept ISO date or datetime
    return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return d; }
};

// Returns true when fecha_actualizacion is older than 6 months
const isStale = (d) => {
  if (!d) return false;
  try {
    const then = new Date(d);
    if (isNaN(then.getTime())) return false;
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    return then < sixMonthsAgo;
  } catch { return false; }
};

// Days since a date, or null if invalid/empty
const daysSince = (d) => {
  if (!d) return null;
  try {
    const then = new Date(d);
    if (isNaN(then.getTime())) return null;
    const diff = Math.floor((Date.now() - then.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  } catch { return null; }
};

/**
 * Reusable price-base section. Used inside SettingsPage as a tab.
 * Can also be rendered standalone via its default export (kept for deep links).
 */
export const PriceBaseSection = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('');
  const [soloConPrecio, setSoloConPrecio] = useState(true);
  // Filtra visualmente los registros con actualización > 90 días (o sin fecha).
  // No hace una nueva petición al backend — se aplica sobre `items` ya cargados.
  const [soloCaducados, setSoloCaducados] = useState(false);
  // editing[itemId] = { [field]: value }
  const [editing, setEditing] = useState({});
  // Idealista paste modal
  const [pasteModal, setPasteModal] = useState(null); // { item, url }
  const [pasteText, setPasteText] = useState('');
  const [pasteLoading, setPasteLoading] = useState(false);
  const [cleaningAlquiler, setCleaningAlquiler] = useState(false);

  const handleCleanupInvalidAlquiler = async () => {
    // 1) Dry run: preview affected rows before touching anything.
    setCleaningAlquiler(true);
    try {
      const dry = await axios.post(
        `${API}/price-m2-base/cleanup-invalid-alquiler?dry_run=true`,
        {},
        { withCredentials: true },
      );
      const n = dry.data?.count || 0;
      if (n === 0) {
        toast.success('Todos los precios de alquiler están dentro de rango realista.');
        return;
      }
      const preview = (dry.data.items || []).slice(0, 5)
        .map(it => `• ${it.municipio || it.provincia}: ${it.precio_m2_alquiler} €/m²`)
        .join('\n');
      const ok = window.confirm(
        `${n} registros tienen precio_m2_alquiler fuera de rango (>100 o <0.5 €/m²).\n\n` +
        `Ejemplos:\n${preview}${n > 5 ? `\n… y ${n - 5} más` : ''}\n\n` +
        `¿Vaciar el campo para que puedas re-scrapearlos con "Idealista"?`
      );
      if (!ok) return;
      const res = await axios.post(
        `${API}/price-m2-base/cleanup-invalid-alquiler?dry_run=false`,
        {},
        { withCredentials: true },
      );
      toast.success(`${res.data.updated} registros corregidos. Vuelve a scrapear "Idealista" para actualizarlos.`);
      fetchData(filter, soloConPrecio);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al limpiar alquileres');
    } finally {
      setCleaningAlquiler(false);
    }
  };

  const fetchData = async (text = '', onlyPriced = soloConPrecio) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (text) params.set('q', text);
      if (onlyPriced) params.set('solo_con_precio', 'true');
      const res = await axios.get(`${API}/price-m2-base?${params}`, { withCredentials: true });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => fetchData(filter, soloConPrecio);

  const toggleSoloConPrecio = () => {
    const next = !soloConPrecio;
    setSoloConPrecio(next);
    fetchData(filter, next);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/price-m2-base/import`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${res.data.imported} registros importados. ${res.data.assets_updated} activos actualizados.`);
      fetchData();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally { setUploading(false); }
  };

  const handleUploadMunicipios = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/municipios/import`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      toast.success(`${res.data.provincias} provincias procesadas. ${res.data.municipios_added} municipios nuevos añadidos.`);
      fetchData();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally { setUploading(false); }
  };

  // Open Idealista page in new tab and show paste modal for that row
  const openPasteModal = (item, opts = {}) => {
    // Priority: saved idealista_url (user-corrected) > auto-generated
    const auto = item.zona_barrio
      ? idealistaMunUrl(item.provincia, item.municipio)
      : (item.municipio ? idealistaMunUrl(item.provincia, item.municipio) : idealistaProvUrl(item.provincia));
    const url = item.idealista_url || auto;
    if (!url) { toast.error('No se puede construir URL de Idealista para este registro'); return; }
    window.open(url, '_blank', 'noopener,noreferrer');
    setPasteText('');
    setPasteModal({ item, url, autoUrl: auto, assistant: !!opts.assistant });
  };

  // Encuentra la próxima fila caducada (>90 días o sin fecha) y arranca el
  // asistente encadenado sobre ella. Salta la fila ya procesada (`skipId`).
  const openNextStale = (skipId) => {
    const next = items.find(it => {
      if (it.id === skipId) return false;
      const d = daysSince(it.fecha_actualizacion);
      return d === null || d > 90;
    });
    if (!next) {
      toast.success('🎉 Todas las filas caducadas están actualizadas.');
      return;
    }
    openPasteModal(next, { assistant: true });
  };

  // Called when user edits the URL in the modal
  const updateModalUrl = (newUrl) => {
    setPasteModal(prev => prev ? { ...prev, url: newUrl } : prev);
  };

  const reopenIdealista = () => {
    if (pasteModal?.url) window.open(pasteModal.url, '_blank', 'noopener,noreferrer');
  };

  const closePasteModal = () => {
    setPasteModal(null);
    setPasteText('');
    setPasteLoading(false);
  };

  const submitPaste = async (overrideText) => {
    if (!pasteModal?.item) return;
    const textToUse = (overrideText ?? pasteText ?? '').trim();
    if (textToUse.length < 50) {
      toast.error('Pega el contenido completo de la página de Idealista');
      return;
    }
    setPasteLoading(true);
    try {
      // Include URL override if the user modified it
      const urlChanged = pasteModal.url && pasteModal.url !== pasteModal.autoUrl;
      const payload = { raw_text: textToUse };
      if (urlChanged) payload.idealista_url = pasteModal.url;
      const res = await axios.post(
        `${API}/price-m2-base/${pasteModal.item.id}/parse-idealista`,
        payload,
        { withCredentials: true }
      );
      const ap = res.data.aplicado || {};
      const count = Object.keys(ap).length;
      const muns = res.data.municipios_actualizados || 0;
      const aff = res.data.affected_assets || 0;
      let msg = `Fila actualizada · ${count} precio(s) detectado(s)`;
      if (muns > 0) msg += ` · ${muns} municipio(s) actualizado(s) en bulk`;
      if (aff > 0) msg += ` · ${aff} activo(s) recalculado(s)`;
      toast.success(msg, { duration: 5000 });
      const wasAssistant = pasteModal.assistant === true;
      // El id procesado — para saltarlo al buscar la próxima caducada.
      const doneId = pasteModal.item.id;
      closePasteModal();
      await fetchData(filter);
      // Encadenado del modo asistente: buscar siguiente caducada y abrirla.
      if (wasAssistant) {
        setTimeout(() => openNextStale(doneId), 400);
      }
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally { setPasteLoading(false); }
  };

  // Lee el portapapeles del usuario y dispara `submitPaste` con ese contenido.
  // El navegador exige un gesto de usuario (click) para acceder al portapapeles;
  // por eso lo llamamos desde el `onClick` del botón. Fallback si el usuario
  // niega el permiso: mostramos el texto en el textarea para que revise.
  const pasteFromClipboardAndSubmit = async () => {
    if (!navigator.clipboard || !navigator.clipboard.readText) {
      toast.error('Tu navegador no permite leer el portapapeles automáticamente. Pega manualmente en el área de texto.');
      return;
    }
    try {
      const text = await navigator.clipboard.readText();
      if (!text || text.trim().length < 50) {
        toast.error('El portapapeles está vacío o el contenido es demasiado corto. Copia la página de Idealista (Ctrl+A · Ctrl+C).');
        setPasteText(text || '');
        return;
      }
      setPasteText(text);
      // Guardamos automáticamente: usa el texto recién leído (no dependemos del re-render).
      await submitPaste(text);
    } catch (e) {
      // Permission denied o error de seguridad → dejamos que el usuario lo pegue manualmente.
      toast.error('No se pudo leer el portapapeles (permiso denegado). Pega manualmente y pulsa "Procesar y guardar".');
    }
  };

  const startEdit = (item, field) => {
    setEditing(prev => ({
      ...prev,
      [item.id]: { ...(prev[item.id] || {}), [field]: item[field] ?? '' },
    }));
  };

  const setEditValue = (itemId, field, value) => {
    setEditing(prev => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || {}), [field]: value },
    }));
  };

  const cancelEdit = (itemId, field) => {
    setEditing(prev => {
      const row = { ...(prev[itemId] || {}) };
      delete row[field];
      const next = { ...prev };
      if (Object.keys(row).length === 0) delete next[itemId]; else next[itemId] = row;
      return next;
    });
  };

  const saveField = async (itemId, field) => {
    const raw = editing[itemId]?.[field];
    try {
      const payload = {};
      if (field === 'fecha_actualizacion') {
        payload[field] = raw || null;
      } else {
        payload[field] = raw === '' || raw == null ? null : parseFloat(raw);
      }
      const r = await axios.put(`${API}/price-m2-base/${itemId}`, payload, { withCredentials: true });
      const n = r.data?.affected_assets ?? 0;
      if (n > 0) {
        toast.success(`Guardado · ${n} activo${n === 1 ? '' : 's'} recalculado${n === 1 ? '' : 's'} con el nuevo precio`);
      } else {
        toast.success('Guardado');
      }
      cancelEdit(itemId, field);
      fetchData(filter);
    } catch { toast.error('Error al guardar'); }
  };

  const filledCount = items.filter(i =>
    i.precio_m2 != null || i.precio_m2_vivienda != null || i.precio_m2_piso != null || i.precio_m2_casa != null || i.precio_m2_alquiler != null
  ).length;

  const renderPriceCell = (item, field) => {
    const isEditing = editing[item.id]?.[field] !== undefined;
    const val = item[field];
    if (isEditing) {
      return (
        <div className="flex items-center justify-end gap-1">
          <input
            type="number"
            className="w-20 px-2 py-1 border border-blue-300 rounded text-right text-sm"
            value={editing[item.id][field]}
            onChange={e => setEditValue(item.id, field, e.target.value)}
            autoFocus
            onKeyDown={e => e.key === 'Enter' && saveField(item.id, field)}
            data-testid={`price-edit-${field}-${item.id}`}
          />
          <button onClick={() => saveField(item.id, field)} className="text-xs text-blue-600 hover:underline" data-testid={`price-save-${field}-${item.id}`}>OK</button>
          <button onClick={() => cancelEdit(item.id, field)} className="text-xs text-slate-400 hover:underline">X</button>
        </div>
      );
    }
    // Per-field staleness: use precio_m2_XXX_at with fallback to row's fecha_actualizacion
    const stampedAt = item[field + '_at'] || item.fecha_actualizacion;
    const days = val != null ? daysSince(stampedAt) : null;
    const stale = days != null && days > 180;
    const cls = val == null
      ? 'text-slate-300'
      : stale
        ? 'text-red-600 font-semibold'
        : 'text-slate-800 font-medium';
    return (
      <span
        className={`cursor-pointer hover:underline whitespace-nowrap inline-flex items-center gap-1 ${cls}`}
        onClick={() => startEdit(item, field)}
        title={stale ? `Actualización hace ${days} días. Recomendable refrescar.` : undefined}
        data-testid={`price-cell-${field}-${item.id}`}
      >
        {val != null ? `${formatPrice(val)} €/m2` : 'Sin precio'}
        {stale && (
          <span
            className="text-[10px] uppercase tracking-wide bg-red-100 text-red-700 px-1.5 py-0.5 rounded"
            data-testid={`stale-field-${field}-${item.id}`}
          >
            +{days}d
          </span>
        )}
      </span>
    );
  };

  const renderDateCell = (item) => {
    const field = 'fecha_actualizacion';
    const isEditing = editing[item.id]?.[field] !== undefined;
    const stale = isStale(item.fecha_actualizacion);
    if (isEditing) {
      return (
        <div className="flex items-center gap-1">
          <input
            type="date"
            className="px-2 py-1 border border-blue-300 rounded text-xs"
            value={editing[item.id][field] || ''}
            onChange={e => setEditValue(item.id, field, e.target.value)}
            autoFocus
            data-testid={`price-edit-fecha-${item.id}`}
          />
          <button onClick={() => saveField(item.id, field)} className="text-xs text-blue-600 hover:underline">OK</button>
          <button onClick={() => cancelEdit(item.id, field)} className="text-xs text-slate-400 hover:underline">X</button>
        </div>
      );
    }
    const baseCls = item.fecha_actualizacion
      ? (stale ? 'text-amber-600 font-medium' : 'text-slate-600')
      : 'text-slate-300';
    return (
      <span
        className={`cursor-pointer hover:underline text-xs inline-flex items-center gap-1 ${baseCls}`}
        onClick={() => startEdit(item, field)}
        title={stale ? 'Dato con más de 6 meses. Considera refrescarlo.' : undefined}
        data-testid={`price-cell-fecha-${item.id}`}
      >
        {formatDate(item.fecha_actualizacion)}
        {stale && <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded" data-testid={`stale-badge-${item.id}`}>desactualizado</span>}
      </span>
    );
  };

  // Filtro cliente: en modo "solo caducados" ocultamos filas con < 90 días.
  // Rows sin fecha se consideran caducadas también (nunca actualizadas).
  const visibleItems = soloCaducados
    ? items.filter(it => {
        const d = daysSince(it.fecha_actualizacion);
        return d === null || d > 90;
      })
    : items;

  return (
    <div className="space-y-6" data-testid="price-base-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="heading-2 flex items-center gap-2"><Database className="w-5 h-5 text-blue-600" /> Base de Precios m²</h2>
          <p className="body-text mt-1">{total} registros ({filledCount} con precio)</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              // Activa el filtro Y arranca el asistente encadenado en la primera fila caducada.
              setSoloCaducados(true);
              setSoloConPrecio(false);
              // Delay corto para que el filtro se aplique y `items` esté sincronizado.
              setTimeout(() => openNextStale(null), 250);
            }}
            className="btn-secondary text-sm flex items-center gap-2"
            title="Filtra las filas caducadas y arranca el asistente: al guardar cada fila, se abre automáticamente la siguiente."
            data-testid="refresh-stale-btn"
          >
            <RefreshCcw className="w-4 h-4" />
            Refrescar &gt;90 días
          </button>
          <button
            onClick={handleCleanupInvalidAlquiler}
            disabled={cleaningAlquiler}
            className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-50"
            title="Limpia valores de alquiler fuera de rango (>100 €/m²) heredados de bulk-pastes antiguos"
            data-testid="cleanup-alquiler-btn"
          >
            {cleaningAlquiler ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            Corregir alquileres inválidos
          </button>
          <label className={`btn-secondary flex items-center gap-2 cursor-pointer text-sm ${uploading ? 'opacity-50' : ''}`}>
            <Upload className="w-4 h-4" />
            Importar Municipios
            <input type="file" accept=".xlsx,.xls" onChange={e => handleUploadMunicipios(e.target.files[0])} className="hidden" disabled={uploading} data-testid="municipios-upload" />
          </label>
          <label className={`btn-primary flex items-center gap-2 cursor-pointer ${uploading ? 'opacity-50' : ''}`}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Importando...' : 'Importar Precios'}
            <input type="file" accept=".xlsx,.xls,.csv" onChange={e => handleUpload(e.target.files[0])} className="hidden" disabled={uploading} data-testid="price-upload" />
          </label>
        </div>
      </div>

      {/* Search */}
      <div className="card-base p-4 flex gap-3 items-center flex-wrap">
        <div className="flex-1 relative min-w-[240px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input className="input-base pl-9" value={filter} onChange={e => setFilter(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} placeholder="Filtrar por provincia, municipio o barrio..." data-testid="price-filter" />
        </div>
        <button onClick={handleSearch} className="btn-secondary" data-testid="price-search-btn">Buscar</button>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none" data-testid="toggle-solo-con-precio">
          <input
            type="checkbox"
            checked={soloConPrecio}
            onChange={toggleSoloConPrecio}
            className="w-4 h-4 accent-blue-600"
          />
          Solo con precio
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none" data-testid="toggle-solo-caducados">
          <input
            type="checkbox"
            checked={soloCaducados}
            onChange={e => setSoloCaducados(e.target.checked)}
            className="w-4 h-4 accent-amber-600"
          />
          Solo caducados (&gt;90 días)
        </label>
        {soloCaducados && (
          <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            Mostrando {visibleItems.length} de {items.length} caducados en la página. Usa el botón <span className="font-medium">Idealista</span> por fila para refrescar.
          </span>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
      ) : (
        <div className="card-base" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto', maxHeight: '600px', overflowY: 'auto' }}>
            <table className="w-full text-sm" style={{ minWidth: '1250px' }}>
              <thead>
                <tr className="bg-slate-50" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase bg-slate-50">Provincia</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase bg-slate-50">Municipio</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase bg-slate-50">Zona / Barrio</th>
                  {PRICE_COLS.map(col => (
                    <th key={col.key} className="px-3 py-3 text-right text-xs font-medium text-slate-500 uppercase bg-slate-50">{col.label}</th>
                  ))}
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase bg-slate-50">Actualizado</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase bg-slate-50">Nivel</th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase bg-slate-50">Acción</th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map(item => {
                  const provUrl = idealistaProvUrl(item.provincia);
                  const munUrl = idealistaMunUrl(item.provincia, item.municipio);
                  return (
                  <tr key={item.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-3 py-2.5">
                      {provUrl ? (
                        <a
                          href={provUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-700 hover:text-blue-600 hover:underline inline-flex items-center gap-1 group"
                          title={`Abrir Idealista · ${item.provincia}`}
                          data-testid={`idealista-prov-${item.id}`}
                        >
                          {item.provincia}
                          <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </a>
                      ) : (
                        <span className="text-slate-700">{item.provincia}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {munUrl && item.municipio ? (
                        <a
                          href={munUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-700 font-medium hover:text-blue-600 hover:underline inline-flex items-center gap-1 group"
                          title={`Abrir Idealista · ${item.municipio}`}
                          data-testid={`idealista-mun-${item.id}`}
                        >
                          {item.municipio}
                          <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </a>
                      ) : (
                        <span className="text-slate-700 font-medium">{item.municipio || '-'}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-slate-500">{item.zona_barrio || '-'}</td>
                    {PRICE_COLS.map(col => (
                      <td key={col.key} className="px-3 py-2.5 text-right">{renderPriceCell(item, col.key)}</td>
                    ))}
                    <td className="px-3 py-2.5">{renderDateCell(item)}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${item.nivel_fuente === 'barrio' ? 'bg-emerald-100 text-emerald-700' : item.nivel_fuente === 'municipio' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                        {item.nivel_fuente || '-'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <button
                        onClick={() => openPasteModal(item)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-600 hover:text-white hover:bg-blue-600 border border-blue-200 hover:border-blue-600 rounded-md transition-colors"
                        title="Actualizar desde Idealista"
                        data-testid={`scrape-btn-${item.id}`}
                      >
                        <RefreshCcw className="w-3 h-3" />
                        Idealista
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Idealista Paste Modal */}
      {pasteModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closePasteModal}
          data-testid="paste-modal-backdrop"
        >
          <div
            className="rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col"
            style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
            onClick={e => e.stopPropagation()}
            data-testid="paste-modal"
          >
            {/* Header */}
            <div className="flex items-start justify-between p-6" style={{ borderBottom: '1px solid var(--color-border)' }}>
              <div className="flex-1">
                <h3 className="heading-3 flex items-center gap-2">
                  <RefreshCcw className="w-5 h-5 text-blue-600" />
                  Actualizar desde Idealista
                  {pasteModal.assistant && (
                    <span
                      className="text-[10px] uppercase tracking-wide bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold"
                      title="Al guardar se abrirá automáticamente la siguiente fila caducada"
                      data-testid="assistant-badge"
                    >
                      Asistente encadenado
                    </span>
                  )}
                </h3>
                <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  {pasteModal.item.provincia}
                  {pasteModal.item.municipio && ` · ${pasteModal.item.municipio}`}
                  {pasteModal.item.zona_barrio && ` · ${pasteModal.item.zona_barrio}`}
                </p>
              </div>
              <button
                onClick={closePasteModal}
                className="p-1 hover:opacity-80"
                style={{ color: 'var(--color-text-muted)' }}
                data-testid="paste-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4 overflow-y-auto">
              <div
                className="rounded-lg p-4 text-sm"
                style={{
                  background: 'color-mix(in oklab, var(--color-blue-accent) 12%, transparent)',
                  border: '1px solid color-mix(in oklab, var(--color-blue-accent) 35%, transparent)',
                  color: 'var(--color-text-primary)',
                }}
              >
                <p className="font-semibold mb-2">Instrucciones</p>
                <ol className="list-decimal list-inside space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
                  <li>Se ha abierto Idealista en otra pestaña</li>
                  <li>En esa pestaña, pulsa <kbd className="px-1.5 py-0.5 rounded text-xs font-mono" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>Ctrl</kbd> + <kbd className="px-1.5 py-0.5 rounded text-xs font-mono" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>A</kbd> para seleccionar todo y <kbd className="px-1.5 py-0.5 rounded text-xs font-mono" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>Ctrl</kbd> + <kbd className="px-1.5 py-0.5 rounded text-xs font-mono" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>C</kbd> para copiar</li>
                  <li>Vuelve aquí y pega (<kbd className="px-1.5 py-0.5 rounded text-xs font-mono" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>Ctrl</kbd> + <kbd className="px-1.5 py-0.5 rounded text-xs font-mono" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>V</kbd>) el contenido abajo</li>
                  <li>Pulsa "Procesar" y detectaremos automáticamente los precios</li>
                </ol>
                <p className="mt-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  💡 Si pegas una <strong>página de provincia</strong>, también actualizaremos automáticamente los municipios listados en la tabla de precios (venta + alquiler) al final de la página.
                </p>
              </div>

              {/* Editable URL */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-primary)' }}>
                  URL de Idealista
                  {pasteModal.url !== pasteModal.autoUrl && pasteModal.autoUrl && (
                    <span className="ml-2 text-xs font-normal text-amber-500">(modificada — se guardará al procesar)</span>
                  )}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={pasteModal.url || ''}
                    onChange={e => updateModalUrl(e.target.value)}
                    className="input-base flex-1 text-sm font-mono"
                    placeholder="https://www.idealista.com/valoracion-de-inmuebles/..."
                    data-testid="paste-url-input"
                  />
                  <button
                    type="button"
                    onClick={reopenIdealista}
                    className="btn-secondary flex items-center gap-1.5 whitespace-nowrap"
                    title="Abrir esta URL en nueva pestaña"
                    data-testid="paste-url-reopen"
                  >
                    <ExternalLink className="w-4 h-4" /> Abrir
                  </button>
                </div>
                {pasteModal.url !== pasteModal.autoUrl && pasteModal.autoUrl && (
                  <button
                    type="button"
                    onClick={() => updateModalUrl(pasteModal.autoUrl)}
                    className="text-xs mt-1 underline hover:opacity-80"
                    style={{ color: 'var(--color-text-muted)' }}
                    data-testid="paste-url-reset"
                  >
                    Volver a la URL automática
                  </button>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-primary)' }}>Contenido pegado</label>
                <div className="rounded-lg p-3 mb-2 text-xs" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-muted)' }}>
                  <p className="font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>Flujo rápido</p>
                  <ol className="list-decimal ml-4 space-y-0.5">
                    <li>Se ha abierto Idealista en otra pestaña.</li>
                    <li>Allí pulsa <kbd className="px-1 rounded bg-slate-700 text-slate-100">Ctrl+A</kbd> y <kbd className="px-1 rounded bg-slate-700 text-slate-100">Ctrl+C</kbd>.</li>
                    <li>Vuelve aquí y pulsa <span className="font-medium">Pegar del portapapeles y guardar</span>: se guarda sin pasar por el textarea.</li>
                  </ol>
                </div>
                <textarea
                  value={pasteText}
                  onChange={e => setPasteText(e.target.value)}
                  placeholder="Pega aquí el contenido de la página de Idealista (o usa 'Pegar del portapapeles y guardar' abajo)..."
                  className="input-base w-full h-56 text-sm font-mono resize-none"
                  data-testid="paste-textarea"
                />
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  {pasteText.length} caracteres
                  {pasteText.length > 0 && pasteText.length < 50 && <span className="text-amber-500 ml-2">Demasiado corto</span>}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div
              className="flex items-center justify-end gap-3 p-6 rounded-b-2xl"
              style={{ borderTop: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)' }}
            >
              <button
                onClick={closePasteModal}
                className="btn-secondary"
                disabled={pasteLoading}
                data-testid="paste-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={pasteFromClipboardAndSubmit}
                disabled={pasteLoading}
                className="btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Lee lo que tengas copiado (Ctrl+A · Ctrl+C en la página de Idealista) y guarda directamente sin pasar por el textarea."
                data-testid="paste-from-clipboard"
              >
                {pasteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardPaste className="w-4 h-4" />}
                Pegar del portapapeles y guardar
              </button>
              <button
                onClick={() => submitPaste()}
                disabled={pasteLoading || pasteText.trim().length < 50}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="paste-submit"
              >
                {pasteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
                {pasteLoading ? 'Procesando...' : 'Procesar y guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Standalone page (kept for deep links /precios-base; redirects to /parametros?tab=precios via Settings)
const PriceBasePage = () => <PriceBaseSection />;
export default PriceBasePage;
